import { supabase } from '@/lib/supabase';
import { GroupActivity, GroupActivityParticipant } from '@/types/groupActivity';

/**
 * Fetches all group activities, including full participant objects
 */
export const fetchGroupActivities = async (): Promise<GroupActivity[]> => {
  try {
    // 1) Get current user's auth info
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // 2) Lookup their database ID
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    if (userError) throw userError;
    if (!userData) throw new Error('User not found');

    // 3) Call the RPC to get basic activity info + counts
    const { data: rows, error: rpcError } = await supabase.rpc(
      'get_group_activities_with_participants',
      { user_id_param: userData.id }
    );
    if (rpcError) throw rpcError;
    if (!rows) return [];

    // 4) Build base activities array (no participants yet)
    const baseActivities = (rows as any[])
      .map(item => ({
        id: item.id,
        creatorId: item.creator_id,
        creatorName: item.creator_name,
        creatorAvatar: item.creator_avatar,
        title: item.title,
        description: item.description,
        sport: item.sport,
        groupDate: item.group_date,
        groupTime: item.group_time,
        venueName: item.venue_name,
        venueAddress: item.venue_address,
        venueCity: item.venue_city,
        maxParticipants: item.max_participants,
        currentParticipants: item.current_participants,
        status: item.status,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        userStatus: item.user_status,
        isCreator: item.creator_id === userData.id
      }))
      .filter(a => a.status !== 'cancelled');

    if (baseActivities.length === 0) {
      return baseActivities as GroupActivity[];
    }

        // 5) Fetch all participants for these activities
    const activityIds = baseActivities.map(a => a.id);
    const { data: partRows, error: partError } = await supabase
      .from('group_activity_participants')
      .select(`
        id,
        group_activity_id,
        status,
        created_at,
        users:user_id (
          id,
          auth_id,
          full_name,
          avatar_url
        )
      `)
      .in('group_activity_id', activityIds);
    if (partError) throw partError;

    // 6) Group participants by activity ID
    const participantsMap: Record<string, GroupActivityParticipant[]> = {};
    (partRows || []).forEach((p: any) => {
      const arr = participantsMap[p.group_activity_id] ||= [];
      arr.push({
        id: p.id,
        userId: p.users.id,
        userName: p.users.full_name,
        userAvatar: p.users.avatar_url,
        status: p.status,
        createdAt: p.created_at
      });
    });


    // 7) Attach participants to each activity
    return baseActivities.map(a => ({
      ...a,
      participants: participantsMap[a.id] || []
    }));
  } catch (error) {
    console.error('Error fetching group activities:', error);
    throw error;
  }
};

/**
 * Fetches a single group activity by ID
 */
export const fetchGroupActivityById = async (
  activityId: string
): Promise<GroupActivity> => {
  try {
    // Get current user's auth info
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Lookup their database ID
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    if (userError) throw userError;
    if (!userData) throw new Error('User not found');

    // Call RPC to get activities
    const { data: rows, error: rpcError } = await supabase.rpc(
      'get_group_activities_with_participants',
      { user_id_param: userData.id }
    );
    if (rpcError) throw rpcError;
    if (!rows) throw new Error('Group activity not found');

    // Find the requested activity
    const activity = (rows as any[]).find(item => item.id === activityId);
    if (!activity) throw new Error('Group activity not found');

    // Fetch its participants
    const { data: participantsData, error: participantsError } = await supabase
      .from('group_activity_participants')
      .select(`
        id,
        user_id,
        status,
        created_at,
        users:user_id (
          auth_id,
          full_name,
          avatar_url
        )
      `)
      .eq('group_activity_id', activityId);
    if (participantsError) throw participantsError;

    const participants: GroupActivityParticipant[] = (participantsData || []).map(
      (p: any) => ({
        id: p.id,
        userId: p.users.auth_id,
        userName: p.users.full_name,
        userAvatar: p.users.avatar_url,
        status: p.status,
        createdAt: p.created_at
      })
    );

    // Return the full GroupActivity object
    return {
      id: activity.id,
      creatorId: activity.creator_id,
      creatorName: activity.creator_name,
      creatorAvatar: activity.creator_avatar,
      title: activity.title,
      description: activity.description,
      sport: activity.sport,
      groupDate: activity.group_date,
      groupTime: activity.group_time,
      venueName: activity.venue_name,
      venueAddress: activity.venue_address,
      venueCity: activity.venue_city,
      maxParticipants: activity.max_participants,
      currentParticipants: activity.current_participants,
      status: activity.status,
      createdAt: activity.created_at,
      updatedAt: activity.updated_at,
      userStatus: activity.user_status,
      isCreator: activity.creator_id === userData.id,
      participants
    };
  } catch (error) {
    console.error('Error fetching group activity:', error);
    throw error;
  }
};

/**
 * Creates a new group activity
 */
export const createGroupActivity = async (
  activityData: Partial<GroupActivity>
): Promise<GroupActivity> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    if (userError) throw userError;
    if (!userData) throw new Error('User not found');

    const { data, error } = await supabase
      .from('group_activities')
      .insert({
        creator_id: userData.id,
        title: activityData.title,
        description: activityData.description,
        sport: activityData.sport,
        group_date: activityData.groupDate,
        group_time: activityData.groupTime,
        venue_name: activityData.venueName,
        venue_address: activityData.venueAddress,
        venue_city: activityData.venueCity,
        max_participants: activityData.maxParticipants || 10
      })
      .select()
      .single();
    if (error) throw error;
    if (!data) throw new Error('Failed to create group activity');

    await supabase.rpc('join_group_activity', {
      group_activity_id_param: data.id,
      user_id_param: userData.id
    });

    return {
      id: data.id,
      creatorId: data.creator_id,
      creatorName: user.user_metadata?.full_name || 'User',
      creatorAvatar: user.user_metadata?.avatar_url,
      title: data.title,
      description: data.description,
      sport: data.sport,
      groupDate: data.group_date,
      groupTime: data.group_time,
      venueName: data.venue_name,
      venueAddress: data.venue_address,
      venueCity: data.venue_city,
      maxParticipants: data.max_participants,
      currentParticipants: 1,
      status: data.status,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      userStatus: 'accepted',
      isCreator: true,
      participants: []
    };
  } catch (error) {
    console.error('Error creating group activity:', error);
    throw error;
  }
};

/**
 * Updates an existing group activity
 */
export const updateGroupActivity = async (
  activityId: string,
  updates: Partial<GroupActivity>
): Promise<boolean> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('group_activities')
      .update({
        title: updates.title,
        description: updates.description,
        sport: updates.sport,
        group_date: updates.groupDate,
        group_time: updates.groupTime,
        venue_name: updates.venueName,
        venue_address: updates.venueAddress,
        venue_city: updates.venueCity,
        max_participants: updates.maxParticipants,
        updated_at: new Date().toISOString()
      })
      .eq('id', activityId);
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating group activity:', error);
    throw error;
  }
};

/**
 * Cancels a group activity
 */
export const cancelGroupActivity = async (
  activityId: string
): Promise<boolean> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    if (userError) throw userError;
    if (!userData) throw new Error('User not found');

    const { data, error } = await supabase.rpc('cancel_group_activity', {
      group_activity_id_param: activityId,
      user_id_param: userData.id
    });
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error cancelling group activity:', error);
    throw error;
  }
};

/**
 * Joins a group activity
 */
export const joinGroupActivity = async (
  activityId: string
): Promise<boolean> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    if (userError) throw userError;
    if (!userData) throw new Error('User not found');

    const { data, error } = await supabase.rpc('join_group_activity', {
      group_activity_id_param: activityId,
      user_id_param: userData.id
    });
    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error('Error joining group activity:', error);
    throw error;
  }
};

/**
 * Leaves a group activity
 */
export const leaveGroupActivity = async (
  activityId: string
): Promise<boolean> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    if (userError) throw userError;
    if (!userData) throw new Error('User not found');

    const { data, error } = await supabase.rpc('leave_group_activity', {
      group_activity_id_param: activityId,
      user_id_param: userData.id
    });
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error leaving group activity:', error);
    throw error;
  }
};
